import { type FC, useMemo } from 'react'

import { useUnit } from 'effector-react'
import { twMerge } from 'tailwind-merge'

import ImageNotPurchasedIcon from '@/assets/icons/icon-image-not-purchased.svg'
import ImageNotSentIcon from '@/assets/icons/icon-image-not-sent.svg'
import ImageSentIcon from '@/assets/icons/icon-image-sent.svg'
import ImageNotLoadedIcon from '@/assets/icons/icon-media-not-loaded.svg'
import { Tooltip } from '@/components/ui/tooltip'
import {
  $allMediaFromCache,
  $finishedPullingSentMedia,
  $sentMedia,
} from '@/models/chattingUI/media.ts'
import type { ScriptNode } from '@/modules/scripts/types'

const scriptAttachmentText = {
  'not-sent': '{count} media is attached and has not been sent yet',
  free: '{count} media has already been sent for free',
  bought: '{count} media has already been bought',
  'not-bought': '{count} media has already been sent but not bought',
}

interface ChatScriptMediaCounterProps {
  script: ScriptNode
  className?: string
}

export const ChatScriptMediaCounter: FC<ChatScriptMediaCounterProps> = ({
  script,
  className,
}) => {
  const sentMedia = useUnit($sentMedia)
  const allMediaFromCache = useUnit($allMediaFromCache)
  const finishedPullingSentMedia = useUnit($finishedPullingSentMedia)
  const scriptMediaItems = script.message?.medias

  const scriptMedias = useMemo(
    () =>
      (finishedPullingSentMedia
        ? scriptMediaItems?.map((media) => {
            const mediasFromCache = allMediaFromCache.filter(
              ({ id }) => id === media.mediaId,
            )
            const hasFree = mediasFromCache.some((m) => m.isFree)

            return {
              ...media,
              sentForFree: hasFree,
              bought:
                !hasFree &&
                mediasFromCache.some((m) => !m.isFree && m.isOpened),
              notBought:
                !hasFree &&
                mediasFromCache.some((m) => !m.isFree && !m.isOpened),
            }
          })
        : undefined) || [],
    [allMediaFromCache, finishedPullingSentMedia, scriptMediaItems],
  )

  const freeMediaCount = scriptMedias?.filter((m) => m.sentForFree).length
  const purchasedMediaCount = scriptMedias?.filter((m) => m.bought).length
  const notPurchasedMediaCount = scriptMedias?.filter((m) => m.notBought).length
  const notSentMediaCount = Math.max(
    scriptMedias?.length -
      freeMediaCount -
      purchasedMediaCount -
      notPurchasedMediaCount,
    0,
  )

  return (
    <div className={twMerge('flex gap-1', className)}>
      {!finishedPullingSentMedia &&
        scriptMediaItems &&
        scriptMediaItems.length > 0 && (
          <Tooltip
            content={`Only ${sentMedia.length} media loaded for this fan. Please wait a moment...`}
          >
            <ImageNotLoadedIcon className="h-4 w-4 text-red-500" />
          </Tooltip>
        )}
      {notSentMediaCount > 0 && (
        <Tooltip
          content={scriptAttachmentText['not-sent'].replace(
            '{count}',
            notSentMediaCount.toString(),
          )}
        >
          <ImageNotSentIcon className="h-4 w-4 text-secondary-100" />
        </Tooltip>
      )}
      {freeMediaCount > 0 && (
        <Tooltip
          content={scriptAttachmentText.free.replace(
            '{count}',
            freeMediaCount.toString(),
          )}
        >
          <ImageSentIcon className="h-4 w-4 text-indigo-400" />
        </Tooltip>
      )}
      {purchasedMediaCount > 0 && (
        <Tooltip
          content={scriptAttachmentText.bought.replace(
            '{count}',
            purchasedMediaCount.toString(),
          )}
        >
          <ImageSentIcon className="h-4 w-4 text-success-default" />
        </Tooltip>
      )}
      {notPurchasedMediaCount > 0 && (
        <Tooltip
          content={scriptAttachmentText['not-bought'].replace(
            '{count}',
            notPurchasedMediaCount.toString(),
          )}
        >
          <ImageNotPurchasedIcon className="h-4 w-4 text-red-400" />
        </Tooltip>
      )}
    </div>
  )
}
